import type { BatchOutcomeSummary } from "~/components/BatchResultsDialog";
import type { BackgroundJobFieldsFragment } from "~/gql/queries/backgroundJob.generated";

import {
	Badge,
	Box,
	Button,
	CircularProgress,
	Divider,
	Icon,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	Popover
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BatchResultsDialog from "~/components/BatchResultsDialog";
import { ClearButton, RetryButton } from "~/components/Buttons";
import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { BackgroundJobItemState, FeatureFlagName } from "~/gql/graphqlBase.generated";
import { useGetMyBackgroundJobsQuery, useLazyGetBackgroundJobItemsQuery } from "~/gql/queries/backgroundJob.generated";
import {
	appendProgressSample,
	estimateCountdown,
	finishNotice,
	isFinishedJob,
	isOpenJob,
	listedJobDestination,
	orderedQueue,
	type ProgressSample,
	type QueueBadgeState,
	queueBadge,
	subscribeOpenBackgroundJobQueue
} from "~/helpers/backgroundJobs/backgroundJobQueue";
import useBackgroundJobMutations from "~/hooks/mutations/useBackgroundJobMutations";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import { useSnacks } from "~/providers/SnackProvider";

const POLL_MS = 60_000;
const FAILED_PAGE_SIZE = 100;

const badgeColor = (state: QueueBadgeState): "error" | "primary" | "success" => {
	if (state === "failed") return "error";
	if (state === "done") return "success";
	return "primary";
};

const BackgroundJobQueueButton = () => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const [openSnack] = useSnacks();
	const { isEnabled } = useFeatureFlag(FeatureFlagName.BackgroundJobs);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [failedResult, setFailedResult] = useState<BatchOutcomeSummary | null>(null);
	const samplesRef = useRef(new Map<string, ProgressSample[]>());
	const seenStatus = useRef(new Map<string, string>());
	const primed = useRef(false);
	const [, setSampleVersion] = useState(0);

	const { data, isError, isFetching, refetch } = useGetMyBackgroundJobsQuery(undefined, {
		pollingInterval: POLL_MS,
		skip: !isEnabled
	});
	const { cancelBackgroundJob, clearFinishedBackgroundJobs, dismissBackgroundJob, retryFailedBackgroundJobItems } =
		useBackgroundJobMutations();
	const [loadFailedItems] = useLazyGetBackgroundJobItemsQuery();

	const jobs = data?.myBackgroundJobs ?? [];

	useEffect(() => {
		if (!isEnabled) return;
		let changed = false;
		const now = Date.now();
		for (const job of jobs) {
			const current = samplesRef.current.get(job.jobId) ?? [];
			const next = appendProgressSample(current, job.done, now);
			if (next !== current) {
				samplesRef.current.set(job.jobId, next);
				changed = true;
			}
		}
		if (changed) setSampleVersion((version) => version + 1);
	}, [isEnabled, jobs]);

	useEffect(() => subscribeOpenBackgroundJobQueue(() => setAnchorEl(buttonRef.current)), []);

	useEffect(() => {
		if (!isEnabled || !data) return;
		if (!primed.current) {
			for (const job of jobs) seenStatus.current.set(job.jobId, job.status);
			primed.current = true;
			return;
		}
		for (const job of jobs) {
			const previous = seenStatus.current.get(job.jobId);
			if (previous && isOpenJob({ status: previous }) && isFinishedJob(job)) {
				const notice = finishNotice(job);
				openSnack(t(notice.key, notice.values), job.failed > 0 ? "warning" : "success");
			}
			seenStatus.current.set(job.jobId, job.status);
		}
	}, [data, isEnabled, jobs, openSnack, t]);

	if (!isEnabled) return null;

	const { count, state } = queueBadge(jobs);
	const { finished, inProgress } = orderedQueue(jobs);
	const open = Boolean(anchorEl);
	const running = jobs.filter((job) => job.status === "RUNNING" && !job.dismissedAt);
	const runningTotal = running.reduce((sum, job) => sum + job.total, 0);
	const runningDone = running.reduce((sum, job) => sum + job.done, 0);

	const go = (route: null | string | undefined) => {
		if (!route) return;
		setAnchorEl(null);
		runGuardedExit(() => navigate(route));
	};

	const onViewFailed = async (job: BackgroundJobFieldsFragment) => {
		const result = await loadFailedItems({
			jobId: job.jobId,
			skip: 0,
			state: BackgroundJobItemState.Failed,
			take: FAILED_PAGE_SIZE
		});
		const items = result.data?.backgroundJobItems ?? [];
		setFailedResult({
			failedCount: job.failed,
			failures: items.map((item) => (item.error ? `${item.label}: ${item.error}` : item.label)),
			totalCount: job.total
		});
	};

	const statusLine = (job: BackgroundJobFieldsFragment): string => {
		if (job.status === "QUEUED") return t("backgroundJobs.queued");
		if (job.type === "CREATE_PLAN") return t("backgroundJobs.creatingLots", { done: job.done, total: job.total });
		return t("backgroundJobs.progress", { done: job.done, total: job.total });
	};

	const countdownLine = (job: BackgroundJobFieldsFragment): null | string => {
		const estimate = estimateCountdown(samplesRef.current.get(job.jobId) ?? [], job, job.done);
		if (estimate === "queued" || estimate === "hidden") return null;
		if (estimate === "less-than-a-minute") return t("backgroundJobs.lessThanAMinute");
		return t("backgroundJobs.aboutMinutes", { count: estimate.minutes });
	};

	const renderRow = (job: BackgroundJobFieldsFragment) => {
		const countdown = countdownLine(job);
		const destination = listedJobDestination(job);
		return (
			<ListItem key={job.jobId} sx={{ alignItems: "flex-start", display: "block", px: 1.5, py: 1 }}>
				<Box
					component={destination ? "button" : "div"}
					onClick={destination ? () => go(destination) : undefined}
					sx={{
						background: "none",
						border: 0,
						color: "inherit",
						cursor: destination ? "pointer" : "default",
						display: "block",
						p: 0,
						textAlign: "left",
						width: "100%"
					}}
					type={destination ? "button" : undefined}
				>
					<BodyTextSmall bold>{job.title}</BodyTextSmall>
					<CaptionText color="text.secondary" sx={{ display: "block" }}>
						{statusLine(job)}
						{countdown ? ` · ${countdown}` : ""}
					</CaptionText>
				</Box>
				{isOpenJob(job) && job.total > 0 && (
					<LinearProgress sx={{ mt: 0.5 }} value={Math.min(100, (job.done / job.total) * 100)} variant="determinate" />
				)}
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
					{job.sourceRoute && (
						<Button onClick={() => go(job.sourceRoute)} size="small" sx={{ textTransform: "none" }}>
							{t("backgroundJobs.openSource")}
						</Button>
					)}
					{isOpenJob(job) && !job.isListedOnly && (
						<Button
							onClick={() => {
								void cancelBackgroundJob(job.jobId);
							}}
							size="small"
							sx={{ textTransform: "none" }}
						>
							{t("backgroundJobs.cancel")}
						</Button>
					)}
					{!job.isListedOnly && job.failed > 0 && (
						<Button onClick={() => void onViewFailed(job)} size="small" sx={{ textTransform: "none" }}>
							{t("backgroundJobs.viewFailed")}
						</Button>
					)}
					{!job.isListedOnly && job.failed > 0 && isFinishedJob(job) && (
						<RetryButton onClick={() => void retryFailedBackgroundJobItems(job.jobId)} size="small">
							{t("backgroundJobs.retryFailed")}
						</RetryButton>
					)}
					<Button
						onClick={() => {
							void dismissBackgroundJob(job.jobId, job.type);
						}}
						size="small"
						sx={{ textTransform: "none" }}
					>
						{t("backgroundJobs.dismiss")}
					</Button>
				</Box>
			</ListItem>
		);
	};

	return (
		<>
			<IconButton
				ref={buttonRef}
				aria-expanded={open}
				aria-haspopup="menu"
				aria-label={count > 0 ? t("backgroundJobs.buttonLabelWithCount", { count }) : t("backgroundJobs.buttonLabel")}
				color="inherit"
				onClick={(event) => setAnchorEl(event.currentTarget)}
				size="small"
			>
				<Badge badgeContent={count} color={badgeColor(state)} invisible={count === 0}>
					<Box sx={{ alignItems: "center", display: "inline-flex", justifyContent: "center", position: "relative" }}>
						{state === "running" && (
							<CircularProgress
								size={28}
								sx={{ left: -5, position: "absolute", top: -5 }}
								value={runningTotal > 0 ? Math.min(100, (runningDone / runningTotal) * 100) : undefined}
								variant={runningTotal > 0 ? "determinate" : "indeterminate"}
							/>
						)}
						<Icon>pending_actions</Icon>
					</Box>
				</Badge>
			</IconButton>
			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				onClose={() => setAnchorEl(null)}
				open={open}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box sx={{ maxWidth: 380, minWidth: 320 }}>
					<Box sx={{ px: 1.5, py: 1 }}>
						<BodyTextSmall bold>{t("backgroundJobs.title")}</BodyTextSmall>
					</Box>
					<Divider />
					{isFetching && jobs.length === 0 ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : isError ? (
						<RetryableErrorState dense message={t("backgroundJobs.loadError")} onRetry={() => refetch()} />
					) : jobs.length === 0 || count === 0 ? (
						<Box sx={{ px: 2, py: 3 }}>
							<CaptionText color="text.secondary">{t("backgroundJobs.empty")}</CaptionText>
						</Box>
					) : (
						<>
							{inProgress.length > 0 && (
								<>
									<CaptionText color="text.secondary" sx={{ display: "block", px: 1.5, pt: 1 }}>
										{t("backgroundJobs.inProgress")}
									</CaptionText>
									<List dense disablePadding>
										{inProgress.map(renderRow)}
									</List>
								</>
							)}
							{finished.length > 0 && (
								<>
									<CaptionText color="text.secondary" sx={{ display: "block", px: 1.5, pt: 1 }}>
										{t("backgroundJobs.finished")}
									</CaptionText>
									<List dense disablePadding>
										{finished.map(renderRow)}
									</List>
									<Divider />
									<ClearButton fullWidth onClick={() => void clearFinishedBackgroundJobs()} size="small">
										{t("backgroundJobs.clearFinished")}
									</ClearButton>
								</>
							)}
						</>
					)}
				</Box>
			</Popover>
			<BatchResultsDialog onClose={() => setFailedResult(null)} open={failedResult !== null} result={failedResult} />
		</>
	);
};

export default BackgroundJobQueueButton;
